<script>
    // The game screen on a phone (docs/phone-plan.md): status bar, ticker,
    // opponent ribbon, market, board strip, the tableau as tiles sized to fit,
    // the hand, and the dock with the prompt. Portrait and landscape are the
    // same parts in two grids. It reads the same stores as the desktop board and
    // calls the same actions; STGame shows it instead of the desktop layout.
    import { onMount, onDestroy, tick } from 'svelte';
    import { get } from 'svelte/store';
    import { fly } from 'svelte/transition';
    import { state, ui, res, prefs } from '../stores.js';
    import { getNeutralZone } from '../game.js';
    import { lookAtCards, canActivate, playerResourceTypes, faceupDraw, sdCards, stardateGlory, editStardate } from '../card_actions.js';
    import { cardOwner } from '../parse.js';
    import { howManyBotActions } from '../bot.js';
    import { getNewIcon } from '../card_images.js';
    import { displayMemo } from '../displaymemo.js';
    import TokenEdit from '../TokenEdit.svelte';
    import TokenDisplay from '../TokenDisplay.svelte';
    import LogItem from '../LogItem.svelte';
    import Prompt from '../Prompt.svelte';
    import PhoneTile from './PhoneTile.svelte';
    import Inspector from './Inspector.svelte';
    import { view, offeredIds } from './phone.js';
    import { tileSize } from './face.js';
    import { longPress } from './hold.js';
    import { closeInspector } from './zoom.js';
    import { watchCardFlights } from './card_flights.js';

    let stopFlights;
    onMount(() => { stopFlights = watchCardFlights(); });
    onDestroy(() => { stopFlights?.(); $view = null; });

    // Whose tableau is on screen: yours (the desktop's viewing seat) or a rival's.
    $: vi = $view ?? $ui.viewing;
    $: p = $state.players[vi];
    $: rivals = $state.players.map((x, i) => i).filter(i => i != $ui.viewing);
    $: ribbon = $view == null ? rivals[0] : $ui.viewing;   // the other side of the slide
    $: rp = $state.players[ribbon];
    $: me = $state.players[$ui.viewing];
    let dir = 1;
    // A prompt offering cards brings their side into view.
    $: offered = offeredIds($ui);
    $: followPrompt(offered);
    function followPrompt(ids){
        if(!ids.length) return;
        const s = get(state), seat = get(ui).viewing, now = get(view) ?? seat;
        const owners = new Set(ids.map(id => s.cardLog[id]).filter(i => i?.area == 'players').map(i => i.player));
        // After this update: a store set mid-update is not seen until the next one.
        if(owners.size && !owners.has(now)) tick().then(() => view.set(owners.has(seat) ? null : [...owners][0]));
    }
    function toggleView(){
        dir = $view == null ? 1 : -1;
        $view = $view == null ? (rivals[0] ?? null) : null;
    }

    // Tracks as PlayerView shows them.
    function tracksOf(x){
        if(!x || x.captain == 'Q') return [];
        let t = x.captain == 'Borg Queen' ? ['collective'] : ['research', 'influence', 'military'];
        if($res.modules?.includes('Treachery')) t = [...t, 'treachery'];
        return t;
    }
    const trackIcon = t => ['research', 'influence', 'military'].includes(t) ? getNewIcon(`${t.slice(0, 3)}-skill`) : getNewIcon(t);
    const trackMax = t => t == 'collective' ? $res.collectiveMax : $res.trackMax;

    // Two areas, as on the desktop: the market (four decks, or the combined
    // market's four cards) and the neutral zone (three locations, plus the
    // Badlands when they are out). Each market deck shows how many are left in it.
    $: market = $prefs.separateMarkets
        ? ['people', 'cargo', 'ships', 'allies'].map(d => ({ c: $state.commons[d]?.[0], left: $state.commons[d]?.length ?? 0, pile: `commons:${d}` })).filter(m => m.c)
        : ($state.commons.market_zone ?? []).slice(0, 4).filter(c => c).map(c => ({ c, left: 0, pile: 'commons:market_zone' }));
    $: nz = $state && getNeutralZone();

    // The zones, in the desktop's order.
    const has = (x, d) => x?.decks?.[d] ?? [];
    $: freeman = p && displayMemo(`freeman|${p.captain}`, () => has(p, 'cleanup').length > 0);
    $: zone1 = p ? [...has(p, 'duty').map(c => ({ c, duty: true })), ...(p.captain != 'Q' ? has(p, 'missions') : []).map(c => ({ c })), ...has(p, 'locations').map(c => ({ c }))] : [];
    $: tuckPile = p?.decks?.assimilated ? ['Assim.', 'assimilated'] : p?.decks?.log ? ['Log', 'log'] : null;
    $: zone2 = p ? [...has(p, 'captain').map((c, i) => ({ c, note: i == 0 && tuckPile ? `${tuckPile[0]} ${has(p, tuckPile[1]).length}` : '' })), ...(freeman ? has(p, 'cleanup') : []).map(c => ({ c })), ...has(p, 'fleet').map(c => ({ c }))] : [];
    $: tucks2 = zone2.some(z => z.c?.beamed?.length);

    // Gold ⚡: an activation the player whose turn it is can use now (as Card.svelte's glow).
    function ready(c){
        if(!c || c.activated) return false;
        const cur = $state.players[$state.currentPlayer];
        if(!cur || !(cur.activations > 0 || cur.activations == -1)) return false;
        try { return displayMemo(`canActivate|${c.uniqueID}`, () => canActivate(c)) && cur.captain == cardOwner(c)?.captain; } catch { return false; }
    }

    // Tiles as tall as lets every card fit (face.js tileSize).
    let tW = 360, tH = 300, land = false, W = 375, H = 812;
    $: land = W > H;
    $: cols = land ? 5 : 3;
    $: rows = Math.ceil(zone1.length / cols) + Math.ceil(zone2.length / cols);
    $: fixed = 2 * 18 + 8 + (tucks2 ? 12 + Math.ceil(zone2.length / cols) * 11 : 0);
    $: size = tileSize({ width: tW - 6, height: tH, rows, cols, fixed });

    // The hand: whole cards, never overlapping, narrowing from six, then scrolling.
    let hW = 300;
    $: hand = has(p, 'hand');
    $: staging = has(p, 'play');
    $: handFacedown = p && $prefs.username != p.username;
    $: handW = Math.max(44, Math.min(land ? 54 : 60, Math.floor((hW - 6 * Math.max(0, hand.length + staging.length - 1)) / Math.max(1, hand.length + staging.length))));

    // Market tiles fill the row in portrait; three across in landscape.
    let mW = 360;
    $: marketW = land ? Math.min(40, Math.floor((mW - 22) / 4)) : Math.max(34, Math.min(50, Math.floor((mW - 50 - (market.length + nz.length) * 3) / Math.max(1, market.length + nz.length))));

    // Stardate readout, as the desktop status bar.
    $: sd = sdCards($state, $res);
    $: sdLeft = sd.length > $state.stardatePlayed;
    $: glory = sdLeft ? stardateGlory($state.stardatePlayed) - $state.gloryTaken : 0;

    $: lastLog = $state.log?.length ? $state.log[$state.log.length - 1] : null;
    $: phases = ['control', 'action', 'cleanup'];

    // The prompt sheet sits above the dock; the working log under the status bar.
    let dockH = 96, topH = 72, handH = 64;
</script>

<svelte:window bind:innerWidth={W} bind:innerHeight={H} />

<div class="stcc-phone" class:land use:longPress style="--phone-dock: {dockH}px; --phone-top: {topH}px; --phone-hand: {handH}px;">
    <div class="top" bind:clientHeight={topH}>
        <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
        <div class="status" on:click={e => { if(!e.target.closest('button, .ctl')) $ui.showingLog = !$ui.showingLog; }}>
            <button class="ib" aria-label="Options" on:click={() => $ui.showingOptions = !$ui.showingOptions}>≡</button>
            <span class="turn">T{$state.turn}</span>
            <span class="phase">{#each phases as ph}<i class:on={$state.phase == ph}>{ph.slice(0, 3).toUpperCase()}</i>{/each}</span>
            <span class="who">{$state.players[$state.currentPlayer]?.captain ?? ''}</span>
            {#if land && p}
                <span class="trk ctl">{#each tracksOf(p) as t}<span class="cnt"><img src={trackIcon(t)} alt={t} />{p[t] || 0}</span>{/each}</span>
            {/if}
            {#if land && rp}
                <button class="oppchip ctl" on:click={toggleView}>{$view == null ? rp.captain : 'Back to you'}{#if $prefs.showScores && $view == null} · {rp.score || 0}{/if}</button>
            {/if}
            <span class="sd ctl" on:click={() => editStardate()}>
                {#if sdLeft}<TokenDisplay display={glory} name="glory" size={4} /><small>{$state.stardatePlayed + 1}/{sd.length}</small>{:else}<small>Glory pile empty</small>{/if}
            </span>
            {#if $state.gameEndTurn != -1}<span class="ending">Ends T{$state.gameEndTurn}</span>{/if}
        </div>
        {#if !land}
            <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
            <div class="ticker" on:click={() => $ui.showingLog = !$ui.showingLog}>▸ {#if lastLog}<LogItem log={lastLog} />{/if}</div>
            {#if rp}
                <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
                <div class="opp" class:you={$view != null} on:click={toggleView}>
                    <b>{rp.captain}</b>
                    {#if $prefs.showScores}<span class="vp">{rp.score || 0} VP</span>{/if}
                    {#each tracksOf(rp) as t}<span class="cnt"><img src={trackIcon(t)} alt={t} />{rp[t] || 0}</span>{/each}
                    <span class="cnt"><img src={getNewIcon('dilithium')} alt="dilithium" />{rp.dilithium || 0}</span>
                    <span class="cnt"><img src={getNewIcon('glory')} alt="glory" />{rp.glory || 0}</span>
                    <span class="chev">{$view == null ? '›' : '‹'}</span>
                </div>
            {/if}
        {/if}
    </div>

    <div class="market" bind:clientWidth={mW}>
        <div class="area mkt">
            <div class="ah">MARKET{#if !$prefs.separateMarkets}<b>{$state.commons.market?.length ?? 0}</b>{/if}</div>
            <div class="row">{#each market as m (m.c.uniqueID)}<span data-pile={m.pile}><PhoneTile c={m.c} w={marketW} traits={0} note={m.left > 1 ? `${m.left}` : ''} /></span>{/each}</div>
        </div>
        <div class="area nz">
            <div class="ah">NEUTRAL ZONE<b>{$state.commons.locations?.length ?? 0} left</b></div>
            <div class="row">{#each nz as c (c.uniqueID)}<PhoneTile {c} w={marketW} traits={0} />{/each}</div>
        </div>
        <div class="piles">
            <span class="pile" data-pile="commons:encounters"><img src={getNewIcon('encounter')} alt="encounters" class="inv" />{$state.commons.encounters.length}</span>
            <span class="pile" data-pile="commons:incidents"><img src={getNewIcon('incident')} alt="incidents" class="inv" />{$state.commons.incidents.length}</span>
            <button class="pile" data-pile="commons:junk" on:click={() => lookAtCards($state.commons.junk, true)}>🗑{$state.commons.junk.length}</button>
        </div>
    </div>

    {#if !land && p}
        <div class="board" class:theirs={$view != null}>
            <b>{p.captain}</b>
            {#if $prefs.showScores}<span class="vp">{p.score || 0}</span>{/if}
            <div class="tracks">{#each tracksOf(p) as t}<TokenEdit inc={1} max={trackMax(t)} {p} r={t} size={4} />{/each}</div>
            {#if p.stardateCards?.length}<span class="alert">{'!'.repeat(p.stardateCards.length)}</span>{/if}
        </div>
    {/if}

    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
    <div class="tableau" bind:clientWidth={tW} bind:clientHeight={tH} data-pile-owner={vi} on:click={e => { if(!e.target.closest('.pc')) closeInspector(); }}>
        {#key vi}
            <div class="view" class:theirs={$view != null} in:fly={{ x: dir * 120, duration: 240, opacity: 0 }}>
                <div class="zone">
                    <div class="zh"><img src={getNewIcon('person')} alt="" class="inv" />DUTY · <img src={getNewIcon('location')} alt="" class="inv" />LOCATIONS<b>{has(p, 'locations').length}</b></div>
                    <div class="grid" style="--cols: {cols};">
                        {#each zone1 as z (z.c.uniqueID)}<PhoneTile c={z.c} w={size.w} h={size.h} duty={z.duty} ready={ready(z.c)} />{/each}
                    </div>
                </div>
                <div class="zone">
                    <div class="zh"><img src={getNewIcon('captain')} alt="" class="inv" />CAPTAIN · <img src={getNewIcon('ship')} alt="" class="inv" />FLEET<b>{has(p, 'fleet').length}</b></div>
                    <div class="grid" class:tucks={tucks2} style="--cols: {cols};">
                        {#each zone2 as z (z.c.uniqueID)}<PhoneTile c={z.c} w={size.w} h={size.h} note={z.note} ready={ready(z.c)} />{/each}
                    </div>
                </div>
            </div>
        {/key}
    </div>

    <div class="hand" bind:clientWidth={hW} bind:clientHeight={handH}>
        <div class="piles col">
            <button class="pile" data-pile="{vi}:deck" on:click={() => faceupDraw(p) && lookAtCards(has(p, 'deck'), true)}><img src={getNewIcon('ship')} alt="" class="inv" />{has(p, 'deck').length}</button>
            <button class="pile" data-pile="{vi}:discard" on:click={() => lookAtCards(has(p, 'discard'), true)}>↓{has(p, 'discard').length}</button>
            {#if has(p, 'reserve').length}<button class="pile" data-pile="{vi}:reserve" on:click={() => lookAtCards(has(p, 'reserve'), true)}>R{has(p, 'reserve').length}</button>{/if}
            {#if has(p, 'dev').length}<button class="pile" data-pile="{vi}:dev" on:click={() => lookAtCards(has(p, 'dev'), true)}>D{has(p, 'dev').length}</button>{/if}
        </div>
        <div class="cards" data-pile="{vi}:hand">
            {#if !p?.bot}
                {#each hand as c (c.uniqueID)}<PhoneTile {c} w={handW} traits={0} facedown={handFacedown} />{/each}
            {/if}
            {#if staging.length}
                <span class="sep" title="Staging area"></span>
                {#each staging as c (c.uniqueID)}<PhoneTile {c} w={handW} traits={0} />{/each}
            {/if}
        </div>
    </div>

    <div class="dock" bind:clientHeight={dockH}>
        {#if me}
            <div class="res">
                {#each playerResourceTypes(me) as r}
                    {#if r != 'activations' || $prefs.numActivations != -1}
                        {#if r == 'actions' && me.bot}
                            <TokenDisplay name="actions" display={howManyBotActions(me, $state)} size={4.5} />
                        {:else if !me.bot || r == 'glory' || r == 'dilithium' || me[r]}
                            <TokenEdit p={me} {r} size={4.5} />
                        {/if}
                    {/if}
                {/each}
            </div>
        {/if}
        <div class="prompt"><Prompt phone /></div>
    </div>

    <Inspector />
</div>

<style>
    .stcc-phone { position: fixed; inset: 0; z-index: 10; display: grid; gap: 4px; background: #000; color: #eef2f8; overflow: hidden;
        padding: calc(env(safe-area-inset-top, 0px) + 4px) calc(env(safe-area-inset-right, 0px) + 6px) calc(env(safe-area-inset-bottom, 0px) + 4px) calc(env(safe-area-inset-left, 0px) + 6px);
        grid-template-columns: minmax(0, 1fr);
        grid-template-rows: auto auto auto minmax(0, 1fr) auto auto;
        grid-template-areas: "top" "market" "board" "tableau" "hand" "dock";
        font: 500 12px 'Barlow Condensed', 'Arial Narrow', system-ui, sans-serif;
        user-select: none; -webkit-user-select: none; -webkit-touch-callout: none; touch-action: manipulation; }
    .stcc-phone.land { grid-template-columns: minmax(0, 1fr) 186px; grid-template-rows: auto minmax(0, 1fr) auto;
        grid-template-areas: "top top" "tableau market" "hand dock"; }
    img.inv { filter: invert(1); }

    .top { grid-area: top; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .status { display: flex; align-items: center; gap: 6px; height: 24px; padding: 0 6px; border-radius: 6px; background: #0d1f73; cursor: pointer; white-space: nowrap; overflow: hidden; }
    .ib { width: 24px; height: 20px; border: 0; border-radius: 5px; background: rgba(255,255,255,.12); color: #fff; font-size: 14px; padding: 0; }
    .turn { font: 700 13px 'Antonio', 'Barlow Condensed', sans-serif; }
    .phase { display: flex; gap: 2px; }
    .phase i { font-style: normal; font-size: 10px; padding: 1px 4px; border-radius: 3px; background: rgba(255,255,255,.08); color: #b8c4e8; }
    .phase i.on { background: #f2c14e; color: #1b1404; font-weight: 700; }
    .trk { display: flex; gap: 6px; flex: none; }
    .who { overflow: hidden; text-overflow: ellipsis; color: #cfd8f2; min-width: 0; }
    .sd { margin-left: auto; display: flex; align-items: center; gap: 3px; flex: none; }
    .sd small { color: #b8c4e8; }
    .ending { background: #b3261e; padding: 0 5px; border-radius: 4px; font-weight: 700; }
    .oppchip { border: 0; border-radius: 5px; background: #8b0000; color: #fff; font: 700 12px 'Barlow Condensed', sans-serif; height: 18px; padding: 0 6px; flex: none; }
    .ticker { height: 16px; font-size: 11.5px; color: #b9c4dc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; padding: 0 4px; }
    .ticker :global(*) { display: inline; white-space: nowrap; }
    .opp { display: flex; align-items: center; gap: 8px; height: 28px; padding: 0 8px; border-radius: 7px; background: #8b0000; cursor: pointer; }
    .opp.you { background: #191970; }
    .opp b { font: 700 14px 'Antonio', 'Barlow Condensed', sans-serif; letter-spacing: .03em; }
    .vp { font: 700 13px 'Antonio', sans-serif; color: #f2c14e; }
    .cnt { display: flex; align-items: center; gap: 2px; font-weight: 600; }
    .cnt img { width: 13px; height: 13px; }
    .chev { margin-left: auto; opacity: .8; font-size: 15px; }

    .market { grid-area: market; display: flex; gap: 4px; align-items: stretch; min-width: 0; }
    .land .market { flex-direction: column; overflow-y: auto; }
    /* Separate areas in the rules, so separate on screen: the market grey, the neutral zone in the location green. */
    .area { display: flex; flex-direction: column; gap: 2px; padding: 2px 3px 3px; border-radius: 6px; background: #1b2232; min-width: 0; }
    .area.nz { background: #12301a; }
    .ah { display: flex; gap: 4px; height: 11px; font-size: 9px; font-weight: 700; letter-spacing: .09em; color: #b9c4dc; white-space: nowrap; }
    .area.nz .ah { color: #9fd8a8; }
    .ah b { margin-left: auto; color: #eef2f8; font-weight: 600; letter-spacing: 0; }
    .row { display: flex; gap: 3px; }
    .sep { width: 1px; align-self: stretch; background: #26324a; margin: 0 2px; flex: none; }
    .piles { display: flex; flex-direction: column; gap: 2px; margin-left: auto; }
    .land .piles { flex-direction: row; margin: 0; }
    .pile { display: flex; align-items: center; gap: 2px; min-width: 28px; padding: 1px 4px; border: 0; border-radius: 4px; background: #141d31; color: #eef2f8; font: 700 10.5px 'Barlow Condensed', sans-serif; }
    .pile img { width: 10px; height: 10px; }

    .board { grid-area: board; display: flex; align-items: center; gap: 8px; min-height: 34px; padding: 2px 8px; border-radius: 7px; background: #191970; overflow: hidden; }
    .board.theirs { background: #4a0b0b; }
    .board b { font: 700 15px 'Antonio', 'Barlow Condensed', sans-serif; letter-spacing: .03em; }
    .tracks { display: flex; gap: 6px; align-items: center; }
    .alert { color: #ff0; font-weight: 700; font-size: 16px; }

    .tableau { grid-area: tableau; position: relative; overflow-y: auto; overflow-x: hidden; min-height: 0; overscroll-behavior: contain; scrollbar-width: none; }
    .tableau::-webkit-scrollbar { display: none; }
    .view { display: flex; flex-direction: column; gap: 4px; }
    .zone { background: #191970; border-radius: 7px; padding: 2px 3px 4px; }
    .view.theirs .zone { background: #4a0b0b; }
    .zh { display: flex; align-items: center; gap: 4px; height: 16px; padding: 0 2px 2px; font-size: 10px; font-weight: 700; letter-spacing: .09em; color: #c7cfe6; }
    .zh img { width: 10px; height: 10px; }
    .zh b { margin-left: auto; color: #eef2f8; }
    .grid { display: grid; grid-template-columns: repeat(var(--cols), minmax(0, 1fr)); gap: 4px; justify-items: start; }
    .grid.tucks { row-gap: 15px; padding-top: 11px; }

    .hand { grid-area: hand; display: flex; gap: 5px; align-items: flex-start; height: 64px; padding: 5px 5px 0; border-radius: 7px 7px 0 0; background: #063f12; overflow: hidden; min-width: 0; position: relative; z-index: 1; }
    .land .hand { height: auto; border-radius: 7px; }
    .piles.col { margin: 0; flex: none; }
    .cards { display: flex; gap: 6px; flex: 1; min-width: 0; overflow-x: auto; scrollbar-width: none; }
    .cards::-webkit-scrollbar { display: none; }

    .dock { grid-area: dock; position: relative; z-index: 2; display: flex; flex-direction: column; gap: 4px; padding: 4px 6px; border-radius: 0 0 7px 7px; border-top: 2px solid rgba(255,255,255,.2); background: #0c1322; max-height: 40vh; overflow-y: auto; }
    .land .dock { border-radius: 7px; }
    .res { display: flex; flex-wrap: wrap; align-items: center; gap: 4px; }
    .prompt { display: flex; min-width: 0; }
</style>
